import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { updateBookData, clearError, setCurrentStep } from '../../../../store/slices/bookSlice';
import { useBookAPI } from '../../../../hooks/useBookAPI';
import axios from 'axios';
import { endpoints } from '../../../../api/api';
import FormFactorModal from './components/FormFactorModal';
import CoverDesignAddon from './components/CoverDesignAddon';
import CoverDesignerHero from './components/CoverDesignerHero';
import { generateCoverTemplate, estimatePageCount, calculateSpineWidth } from './utils/generateCoverTemplate';
import { PAPER_TYPE, BINDING_TYPE, MIN_PAGES, getSuggestedAlternatives } from '../../../../constants/bookFormFactor';
import { useAlert } from '../../../../components/AlertBanner';
import * as pdfjsLib from 'pdfjs-dist';
import {
  trackCoverImageUploaded, trackCoverImageUploadFailed, trackCoverImageRemoved, trackCoverImageRemovalFailed,
  trackCoverDesignAddonAdded, trackCoverDesignAddonRemoved, trackCoverSwitchAlternative,
  trackCoverDesignerOpened, trackCoverStepCompleted,
} from '../../../../utils/analytics';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const getMissingFormFactorFields = (bookData: {
  bookSize?: string;
  bindingType?: string;
  interiorColor?: string;
  paperType?: string;
  coverLamination?: string;
}): string[] => {
  const missing: string[] = [];
  if (!bookData.bookSize) missing.push('Book Size');
  if (!bookData.bindingType) missing.push('Binding Type');
  if (!bookData.interiorColor) missing.push('Print Colour');
  if (!bookData.paperType) missing.push('Paper Type');
  if (!bookData.coverLamination) missing.push('Cover Lamination');
  return missing;
};

const BookCoverStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showFormFactorModal, setShowFormFactorModal] = useState(false);
  const [dismissedPageWarning, setDismissedPageWarning] = useState(false);
  const [switchedToAlternative, setSwitchedToAlternative] = useState(false);
  const [pdfPreviewDataUrl, setPdfPreviewDataUrl] = useState<string | null>(null);
  const [isPdfPreviewLoading, setIsPdfPreviewLoading] = useState(false);

  const bookData = useAppSelector((state) => state.book.bookData);
  const bookStatus = useAppSelector((state) => state.book.bookStatus);
  const bookId = useAppSelector((state) => state.book.bookId);
  const isStep4Complete = useAppSelector((state) => state.book.isStep4Complete);
  const isResubmission = bookStatus === 'rejected';
  const missingFormFactorFields = useMemo(() => getMissingFormFactorFields(bookData), [bookData]);
  const coverDesignAdded = bookData.addons?.coverDesign || false;

  const { saveBook, saveAndContinue, submitForReview, isSaving, error } = useBookAPI();

  const isCoverPdf = !!bookData.coverImage && bookData.coverImage.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    let blobUrl: string | null = null;
    const fetchExistingImage = async () => {
      // PDF covers can't be previewed as an image — skip the fetch
      if (isCoverPdf) {
        setExistingImageUrl(null);
        setImageLoadError(false);
        return;
      }
      if (bookData.coverImage && !previewUrl) {
        setIsLoadingImage(true);
        setImageLoadError(false);
        try {
          const url = endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(bookData.coverImage));
          const response = await axios.get(url, { responseType: 'blob' });
          blobUrl = URL.createObjectURL(response.data);
          setExistingImageUrl(blobUrl);
        } catch (err) {
          setImageLoadError(true);
          setExistingImageUrl(null);
        } finally {
          setIsLoadingImage(false);
        }
      } else if (!bookData.coverImage) {
        setExistingImageUrl(null);
      }
    };
    fetchExistingImage();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [bookData.coverImage, previewUrl, isCoverPdf]);

  // Render full-spread PDF to a preview image whenever isCoverPdf is true
  useEffect(() => {
    if (!isCoverPdf) { setPdfPreviewDataUrl(null); return; }

    let cancelled = false;
    setIsPdfPreviewLoading(true);

    const render = async (arrayBuffer: ArrayBuffer) => {
      const pdfDoc    = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page      = await pdfDoc.getPage(1);
      // Render wide enough to look sharp inside the container (~600px wide target)
      const naturalVp = page.getViewport({ scale: 1 });
      const scale     = Math.max(1.5, 600 / naturalVp.width);
      const vp        = page.getViewport({ scale });
      const canvas    = document.createElement('canvas');
      canvas.width    = vp.width;
      canvas.height   = vp.height;
      await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp, canvas }).promise;
      if (!cancelled) setPdfPreviewDataUrl(canvas.toDataURL());
    };

    const load = async () => {
      try {
        // New upload: previewUrl is a base64 data URL
        if (previewUrl) {
          const base64 = previewUrl.split(',')[1];
          const binary = atob(base64);
          const buf = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
          await render(buf.buffer);
        } else if (bookData.coverImage) {
          // Existing server PDF
          const url = endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(bookData.coverImage));
          const resp = await axios.get(url, { responseType: 'blob' });
          await render(await resp.data.arrayBuffer());
        }
      } catch {
        // Preview failed — silently fall back to the filename card
        if (!cancelled) setPdfPreviewDataUrl(null);
      } finally {
        if (!cancelled) setIsPdfPreviewLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isCoverPdf, previewUrl, bookData.coverImage]);

  const estimatedPageCount = useMemo(
    () => bookData.pageCount || estimatePageCount(bookData.content?.json?.sections),
    [bookData.pageCount, bookData.content],
  );

  const spineWidthInches = useMemo(
    () => calculateSpineWidth(
      estimatedPageCount,
      bookData.paperType || PAPER_TYPE.CREAM,
      bookData.bindingType || BINDING_TYPE.PAPERBACK,
    ),
    [estimatedPageCount, bookData.paperType, bookData.bindingType],
  );

  const minPagesRequired = bookData.bindingType && bookData.paperType
    ? MIN_PAGES[bookData.bindingType]?.[bookData.paperType as keyof (typeof MIN_PAGES)[string]] ?? null
    : null;
  const showPageWarning = !dismissedPageWarning && minPagesRequired !== null && estimatedPageCount < minPagesRequired;
  const suggestedAlternatives = showPageWarning && bookData.bindingType && bookData.paperType
    ? getSuggestedAlternatives(estimatedPageCount, bookData.bindingType, bookData.paperType)
    : [];

  const handleSwitchAlternative = async (bindingType: string, paperType: string) => {
    trackCoverSwitchAlternative(bindingType, paperType);
    dispatch(updateBookData({ bindingType, paperType }));
    setSwitchedToAlternative(true);
    setDismissedPageWarning(false);
    // Pass the new values explicitly — saveBook() reads `bookData` from this
    // component's render-time closure, which won't reflect the dispatch above
    // until the next render.
    await saveBook({ bindingType, paperType });
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  // Uploading here while a Designer-made cover exists silently discards it —
  // this card's own preview doesn't show that cover, so there's no other
  // signal warning the user it's about to be replaced.
  const handleUploadClickGuarded = () => {
    if (isCoverFromDesigner && !window.confirm('Uploading a file will replace your Designer-made cover. Continue?')) return;
    handleUploadClick();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { setUploadError('Please upload a PDF file'); return; }
    if (file.size > 100 * 1024 * 1024) { setUploadError('File size must be less than 100MB'); return; }

    const reader = new FileReader();
    reader.onload = (event) => setPreviewUrl(event.target?.result as string);
    reader.readAsDataURL(file);

    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', 'cover');
      if (bookId) formData.append('bookId', bookId);
      formData.append('coverSource', 'upload');
      const response = await axios.post(endpoints.uploadFile, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        dispatch(updateBookData({ coverImage: response.data.file.filename, coverImageSource: 'upload' }));
        trackCoverImageUploaded(Math.round((file.size / (1024 * 1024)) * 100) / 100);
      }
    } catch (err: any) {
      const reason = err.response?.data?.message || 'Failed to upload image';
      setUploadError(reason);
      trackCoverImageUploadFailed(reason);
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async () => {
    trackCoverImageRemoved();
    setPreviewUrl(null);
    setExistingImageUrl(null);
    dispatch(updateBookData({ coverImage: null, coverImageSource: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (bookId) {
      try {
        await axios.delete(endpoints.removeCoverImage.replace(':id', bookId));
      } catch (err: any) {
        console.error('Failed to remove cover image from server:', err);
        trackCoverImageRemovalFailed(err?.response?.data?.message || err?.message || 'unknown');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const handleViewTemplate = () => {
    if (!bookData.bookSize) {
      showAlert('Please select a Book Size in the Form Factor step first.', 'warning');
      return;
    }
    generateCoverTemplate({
      bookSize: bookData.bookSize,
      paperType: bookData.paperType || PAPER_TYPE.CREAM,
      bindingType: bookData.bindingType || BINDING_TYPE.PAPERBACK,
      sections: bookData.content?.json?.sections,
      pageCount: estimatedPageCount,
    });
  };

  const handleStartDesigning = () => {
    trackCoverDesignerOpened();
    const params = new URLSearchParams();
    if (bookId) params.set('bookId', bookId);
    if (bookData.bookSize) params.set('bookSize', bookData.bookSize);
    if (bookData.title) params.set('title', bookData.title);
    if (bookData.author) params.set('author', bookData.author);
    params.set('paperType', bookData.paperType || PAPER_TYPE.CREAM);
    params.set('bindingType', bookData.bindingType || BINDING_TYPE.PAPERBACK);
    params.set('pageCount', String(estimatedPageCount));
    navigate(`/cover-designer?${params.toString()}`);
  };

  const getImagePreviewUrl = () => previewUrl || existingImageUrl || null;

  const handleSaveToDrafts = async () => {
    const result = await saveBook();
    if (result.meta.requestStatus === 'fulfilled') showAlert('Saved to drafts successfully!', 'success');
  };

  const handleSaveAndContinue = async () => {
    if (missingFormFactorFields.length > 0) { setShowFormFactorModal(true); return; }
    if (!isStep4Complete) { showAlert('Please either upload a cover image or select the Book Cover Design Package.', 'warning'); return; }
    if (isResubmission) { await submitForReview(); return; }
    const result: any = await saveAndContinue('pricing');
    if (result?.meta?.requestStatus !== 'fulfilled') {
      showAlert(
        typeof result?.payload === 'string'
          ? result.payload
          : 'Failed to continue — your page count may have changed, please go back and re-approve it.',
        'error'
      );
    } else {
      trackCoverStepCompleted();
    }
  };

  const handleGoToFormFactor = () => {
    setShowFormFactorModal(false);
    dispatch(setCurrentStep('book-form-factor'));
  };

  const hasCoverPreview = isCoverPdf || (!!getImagePreviewUrl() && !imageLoadError);
  const savedCoverPreviewUrl = isCoverPdf
    ? pdfPreviewDataUrl
    : (getImagePreviewUrl() && !imageLoadError ? getImagePreviewUrl() : null);
  // Has the user ever saved Designer canvas work — independent of which file
  // is currently active. Drives only the hero's CTA label ("Continue" implies
  // there's a canvas to return to, regardless of what's live right now).
  const hasDesignerCanvas = !!bookData.coverDesignerState;
  // Whether the CURRENTLY ACTIVE coverImage came from the Designer or a direct
  // upload — distinct from hasDesignerCanvas above, which stays true even
  // after a later direct upload replaces the active file. Without this
  // distinction, uploading over a Designer cover left both the hero and this
  // card thinking the Designer still owned the file, even though it had just
  // been overwritten.
  // coverImageSource didn't exist before this feature shipped, so every book
  // with a cover from before then has it unset — fall back to hasDesignerCanvas
  // for those (a book that's ever had Designer canvas work is overwhelmingly
  // more likely to have a Designer-made cover than an uploaded one). This is
  // what keeps a pre-existing Designer cover out of the "bring your own file"
  // card's Remove button without needing a database backfill.
  const isCoverFromDesigner = bookData.coverImageSource
    ? bookData.coverImageSource === 'designer'
    : hasDesignerCanvas;
  // Source-agnostic: true whether the file came from the Designer or a direct
  // upload — this is what answers "what will actually be printed," regardless
  // of which of the three options below produced it.
  const hasRealCover = !!bookData.coverImage;

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      <div className="hidden md:block mb-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
          <span>←</span><span>Go Back to Dashboard</span>
        </button>
      </div>

      <div className="md:hidden bg-gray-50 px-4 py-3 border-b border-gray-200">
        <p className="text-xs text-gray-600">Book Info › Book Form Factor › Book Formatting › Book Cover</p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => dispatch(clearError())} className="text-red-600 hover:text-red-800 text-sm underline ml-4">Dismiss</button>
          </div>
        </div>
      )}

      {showFormFactorModal && (
        <FormFactorModal
          missingFields={missingFormFactorFields}
          onClose={() => setShowFormFactorModal(false)}
          onGoToFormFactor={handleGoToFormFactor}
        />
      )}

      {showPageWarning && (
        <div className="mb-4 mx-4 md:mx-0 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-gray-900 text-sm font-medium">Your book doesn't meet the minimum page requirement</p>
              <p className="text-gray-600 text-xs mt-1 leading-relaxed">
                Your book currently has <span className="font-semibold">{estimatedPageCount} pages</span>, but {bookData.bindingType} with {bookData.paperType} requires at least <span className="font-semibold">{minPagesRequired} pages</span>.
                If you add more content later, the spine width will change and your cover will need to be redesigned.
              </p>
              {suggestedAlternatives.length > 0 && (
                <div className="mt-3">
                  <p className="text-gray-700 text-xs font-medium mb-2">Your {estimatedPageCount} pages qualify for:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedAlternatives.map(({ bindingType, paperType }) => (
                      <button
                        key={`${bindingType}-${paperType}`}
                        onClick={() => handleSwitchAlternative(bindingType, paperType)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-full transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Switch to {bindingType} + {paperType}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4 mt-3">
                <button
                  onClick={() => dispatch(setCurrentStep('book-formatting'))}
                  className="text-xs font-medium bg-gray-900 hover:bg-gray-800 text-white px-3 py-1.5 rounded-md transition-colors"
                >
                  Go back and add content
                </button>
                <button
                  onClick={() => setDismissedPageWarning(true)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  I understand, continue anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {switchedToAlternative && !showPageWarning && (
        <div className="mb-4 mx-4 md:mx-0 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <svg className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-blue-700 text-xs leading-relaxed">
            Switched to <span className="font-semibold">{bookData.bindingType} + {bookData.paperType}</span>. Spine dimensions have changed — please re-download the cover template before uploading your cover.
            <button onClick={() => setSwitchedToAlternative(false)} className="ml-2 underline">Dismiss</button>
          </p>
        </div>
      )}

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        <div className="space-y-6">


          <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} className="hidden" />

          {uploadError && (
            <p className="text-red-600 text-xs flex items-center gap-1">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {uploadError}
            </p>
          )}

          <CoverDesignerHero
            onStartDesigning={handleStartDesigning}
            hasExistingCoverDesign={hasDesignerCanvas}
            isCoverFromDesigner={isCoverFromDesigner}
            spineWidthInches={spineWidthInches}
            savedCoverPreviewUrl={savedCoverPreviewUrl}
          />

          <div className="bg-white border border-gray-200 rounded-xl grid grid-cols-1 md:grid-cols-2 divide-y divide-gray-200 md:divide-y-0 md:divide-x">
            <div className="flex flex-col p-6">
              {/* Header: eyebrow + title on the left, Download Template on the right —
                  matching the original page's layout. */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">Bring your own file</p>
                  <label className="block text-gray-900 text-sm font-medium">Upload a print-ready cover</label>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <button
                    onClick={handleViewTemplate}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Template
                  </button>
                  <p className="text-amber-700 text-[10.5px] font-medium">Must match its dimensions exactly</p>
                </div>
              </div>
              <p className="text-gray-400 text-xs mb-6">
                For designers who've already built a front + spine + back flat-lay to the template spec.
              </p>

              {/* Preview — flat-lay shaped (4:3), modestly sized so it doesn't
                  dominate the card; reads as the whole spread, not just a front cover. */}
              <div
                onClick={(!hasCoverPreview || isCoverFromDesigner) && !isLoadingImage ? handleUploadClickGuarded : undefined}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`relative w-full max-w-[240px] mx-auto aspect-[4/3] rounded-lg border overflow-hidden bg-gray-50 flex flex-col items-center justify-center mb-6 ${
                  (!hasCoverPreview || isCoverFromDesigner) && !isLoadingImage ? 'border-dashed border-gray-300 hover:border-gray-400 cursor-pointer' : 'border-gray-200'
                }`}
              >
                {isCoverFromDesigner ? (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <p className="text-gray-500 text-sm">Drag &amp; drop or click to upload</p>
                    <p className="text-gray-400 text-xs">PDF · Max 100MB</p>
                  </div>
                ) : isLoadingImage ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-orange-500" />
                ) : isCoverPdf ? (
                  isPdfPreviewLoading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-orange-500" />
                  ) : pdfPreviewDataUrl ? (
                    <img src={pdfPreviewDataUrl} alt="Cover flat-lay preview" className="absolute inset-0 w-full h-full object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-4">
                      <svg className="w-8 h-8 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      <p className="text-gray-700 text-xs font-medium">Cover PDF ready</p>
                      <p className="text-gray-400 text-[11px] text-center truncate max-w-full">{bookData.coverImage}</p>
                    </div>
                  )
                ) : getImagePreviewUrl() && !imageLoadError ? (
                  <img src={getImagePreviewUrl()!} alt="Cover preview" className="absolute inset-0 w-full h-full object-contain" onError={() => setImageLoadError(true)} />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <p className="text-gray-500 text-sm">Drag &amp; drop or click to upload</p>
                    <p className="text-gray-400 text-xs">PDF · Max 100MB</p>
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-orange-500" />
                  </div>
                )}
              </div>

              {/* Action row — Upload/Replace/Remove, centered to align with
                  the preview window above it. The size requirement now lives
                  in the preview placeholder itself, not competing with the CTA. */}
              {isCoverFromDesigner ? (
                <div className="flex items-center justify-center">
                  <button onClick={handleUploadClickGuarded} disabled={isUploading} className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded-md font-medium text-xs transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {isUploading ? 'Uploading…' : 'Upload PDF'}
                  </button>
                </div>
              ) : isLoadingImage ? null : hasCoverPreview ? (
                <div className="flex items-center justify-center gap-2">
                  <button onClick={handleUploadClick} disabled={isUploading} className="bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded text-xs font-medium shadow-sm hover:bg-gray-50">Replace</button>
                  <button onClick={handleRemoveImage} disabled={isUploading} className="bg-white text-red-500 border border-gray-300 px-3 py-1.5 rounded text-xs font-medium shadow-sm hover:bg-gray-50">Remove</button>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <button onClick={handleUploadClick} disabled={isUploading} className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded-md font-medium text-xs transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    {isUploading ? 'Uploading…' : 'Upload PDF'}
                  </button>
                </div>
              )}

              {imageLoadError && bookData.coverImage && !isCoverFromDesigner && (
                <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Unable to load existing cover. Please upload a new one.
                </p>
              )}
            </div>

            <div className="flex flex-col p-6">
              <CoverDesignAddon
                isAdded={coverDesignAdded}
                selectedType={bookData.addons?.coverDesignType || null}
                onAdd={(type) => {
                  if (hasRealCover && !window.confirm('You already have a cover ready. Adding this package will replace it once our team delivers your new one. Continue?')) return;
                  trackCoverDesignAddonAdded(type);
                  dispatch(updateBookData({ addons: { ...bookData.addons, coverDesign: true, coverDesignType: type } }));
                }}
                onRemove={() => {
                  trackCoverDesignAddonRemoved();
                  dispatch(updateBookData({ addons: { ...bookData.addons, coverDesign: false, coverDesignType: null } }));
                }}
              />
            </div>
          </div>

          <div className="md:hidden flex flex-col items-center space-y-3 pt-4">
            <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-white text-orange-500 px-8 py-3 rounded-md border-2 border-orange-500 font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
            <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-md font-medium disabled:opacity-50">{isSaving ? (isResubmission ? 'Submitting...' : 'Saving...') : (isResubmission ? 'Submit' : 'Continue')}</button>
          </div>

          <div className="hidden md:flex gap-3 pt-4 justify-start">
            <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-transparent text-orange-500 px-8 py-3 rounded-lg border-2 border-orange-500 font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
            <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium disabled:opacity-50">{isSaving ? (isResubmission ? 'Submitting...' : 'Saving...') : (isResubmission ? 'Submit' : 'Continue')}</button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookCoverStep;
